import {AxiosResponse} from 'axios';
import {ActionTree, ActionContext} from 'vuex';
import {AlertNotificationModuleConstant, ClubLocationsModuleConstant, EmployeeProfileModuleConstant, MemberSearchModuleConstant, ProfileModuleConstant, UrlModuleConstant} from '../constants';
import Deferred from '../deferred';
import {clubService} from '../services/clubService';
import {memberSearchService} from '../services/memberSearchService';
import {ConnectionStatus} from '../enums';
import EcCommon from '../../../types/ec/ecCommon';
import EcMemberSearch from '../../../types/ec/ecMemberSearch';
import LtFramework from '../../../types/lt/ltFramework';

export const AlertNotificationActions: ActionTree<LtFramework.IAlertNotificationState, LtFramework.IRootState> = {
	open(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification): Promise<void> {
		return window.ef.alertNotificationStore.open(payload);
	},
	close(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification) {
		return window.ef.alertNotificationStore.close(payload);
	},
	synchronize(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification[]): Promise<boolean> {
		let alertStoreInitialized = false;
		const alertStoreDeferred = new Deferred<boolean>();
		const alertNotifications = window.ef.alertNotificationStore.getInitialData();

		if (!alertStoreInitialized && alertNotifications) {
			action.commit(AlertNotificationModuleConstant.mutation.setAlertNotifications, alertNotifications);

			alertStoreInitialized = true;
			alertStoreDeferred.resolve(alertStoreInitialized);
		}

		window.ef.alertNotificationStore.listen((arr: LtFramework.IAlertNotification[]) => {
			if (typeof arr !== undefined) {
				action.commit(AlertNotificationModuleConstant.mutation.setAlertNotifications, arr);

				if (!alertStoreInitialized) {
					alertStoreInitialized = true;
					alertStoreDeferred.resolve(alertStoreInitialized);
				}
			}
		});

		return alertStoreDeferred.promise;
	},
};

export const ClubLocationsActions: ActionTree<EcCommon.IClubLocationsState, LtFramework.IRootState> = {
	getClubLocations(action: ActionContext<EcCommon.IClubLocationsState, LtFramework.IRootState>): Promise<void> {
		return clubService.getClubLocations()
			.then((response: AxiosResponse<EcCommon.IClubsResponse>) => {
				action.commit(ClubLocationsModuleConstant.mutation.setCountries, response.data.Countries);
			});
	},
	selectClub(action: ActionContext<EcCommon.IClubLocationsState, LtFramework.IRootState>, payload: EcCommon.IMultiSelectGroupOptions): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(ClubLocationsModuleConstant.mutation.setClub, payload);
			resolve();
		});
	},
	unSelectClub(action: ActionContext<EcCommon.IClubLocationsState, LtFramework.IRootState>, payload: EcCommon.IClub): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(ClubLocationsModuleConstant.mutation.removeClub, payload);
			resolve();
		});
	},
	unSelectAllClubs(action: ActionContext<EcCommon.IClubLocationsState, LtFramework.IRootState>): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(ClubLocationsModuleConstant.mutation.unSelectAllClubs);
			resolve();
		});
	},
};

export const EmployeeProfileActions: ActionTree<LtFramework.IEmployeeProfileState, LtFramework.IRootState> = {
	synchronize(action: ActionContext<LtFramework.IEmployeeProfileState, LtFramework.IRootState>): Promise<boolean> {
		let employeeProfileStoreInitialized = false;
		const employeeProfileStoreDeferred = new Deferred<boolean>();
		const employeeProfile = window.ef.employeeProfileStore.getInitialData();

		if (!employeeProfileStoreInitialized && employeeProfile && employeeProfile.initialized) {
			action.commit(EmployeeProfileModuleConstant.mutation.setEmployeeProfile, employeeProfile);

			employeeProfileStoreInitialized = true;
			employeeProfileStoreDeferred.resolve(employeeProfileStoreInitialized);
		}

		window.ef.employeeProfileStore.listen((obj: LtFramework.IEmployeeProfile) => {
			if (obj) {
				action.commit(EmployeeProfileModuleConstant.mutation.setEmployeeProfile, obj);

				if (!employeeProfileStoreInitialized && obj.initialized) {
					employeeProfileStoreInitialized = true;
					employeeProfileStoreDeferred.resolve(employeeProfileStoreInitialized);
				}
			}
		});

		return employeeProfileStoreDeferred.promise;
	},
};

export const MemberSearchActions: ActionTree<EcMemberSearch.IState, LtFramework.IRootState> = {
	setTrainerDetails(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>, payload: EcCommon.ITrainerDetails): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(MemberSearchModuleConstant.mutation.setTrainerDetails, payload);
			resolve();
		});
	},
	searchMembers(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>, payload: EcMemberSearch.ISearchRequestObject): Promise<void> {
		return memberSearchService.searchMembers(payload)
			.then((response: AxiosResponse<EcMemberSearch.ISearchMemberResponse>) => {
				action.commit(MemberSearchModuleConstant.mutation.setMembers, response.data.members);
			});
	},
	clearMembers(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(MemberSearchModuleConstant.mutation.resetMembers);
			resolve();
		});
	},
	searchMemberPhoto(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>, payload: EcMemberSearch.ISearchMemberPhotoRequestObject): Promise<void> {
		return memberSearchService.searchMemberPhotos(payload, true)
			.then((response: AxiosResponse<EcMemberSearch.ISearchMemberPhotoResponse>) => {
				action.commit(MemberSearchModuleConstant.mutation.setMemberPhoto, response.data.memberPhotos);
			});
	},
	searchConnectedStatus(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(MemberSearchModuleConstant.mutation.setConnectStatus);
			resolve();
		});
	},
	setEmailAddressInvalid(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>, payload: number): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(MemberSearchModuleConstant.mutation.setEmailAddressInvalid, payload);
			resolve();
		});
	},
	updateConnectedStatus(action: ActionContext<EcMemberSearch.IState, LtFramework.IRootState>, payload: EcMemberSearch.IMember): Promise<void> {
		return new Promise<void>((resolve) => {
			action.commit(MemberSearchModuleConstant.mutation.updateConnectionStatus, {member: payload, connectionStatus: ConnectionStatus.Connecting});
			setTimeout(() => {
				action.commit(MemberSearchModuleConstant.mutation.updateConnectionStatus, {member: payload, connectionStatus: ConnectionStatus.Connected});
				resolve();
			}, 2000);
		});
	},
};

export const ProfileActions: ActionTree<LtFramework.IProfileState, LtFramework.IRootState> = {
	synchronize(action: ActionContext<LtFramework.IProfileState, LtFramework.IRootState>): Promise<boolean> {
		let profileStoreInitialized = false;
		const profileStoreDeferred = new Deferred<boolean>();
		const profile = window.ef.profileStore.getInitialData();

		if (!profileStoreInitialized && profile && profile.initialized) {
			action.commit(ProfileModuleConstant.mutation.setProfile, profile);

			profileStoreInitialized = true;
			profileStoreDeferred.resolve(profileStoreInitialized);
		}

		window.ef.profileStore.listen((obj: LtFramework.IProfile) => {
			if (obj) {
				action.commit(ProfileModuleConstant.mutation.setProfile, obj);

				if (!profileStoreInitialized && obj.initialized) {
					profileStoreInitialized = true;
					profileStoreDeferred.resolve(profileStoreInitialized);
				}
			}
		});

		return profileStoreDeferred.promise;
	},
};

export const UrlActions: ActionTree<LtFramework.IUrlState, LtFramework.IRootState> = {
	synchronize(action: ActionContext<LtFramework.IUrlState, LtFramework.IRootState>): Promise<boolean> {
		let urlStoreInitialized = false;
		const urlStoreDeferred = new Deferred<boolean>();
		const url = window.ef.urlStore.getInitialData();

		if (!urlStoreInitialized && url && url.initialized) {
			action.commit(UrlModuleConstant.mutation.setUrl, url);

			urlStoreInitialized = true;
			urlStoreDeferred.resolve(urlStoreInitialized);
		}

		window.ef.urlStore.listen((obj: LtFramework.IUrl) => {
			if (obj) {
				action.commit(UrlModuleConstant.mutation.setUrl, obj);

				if (!urlStoreInitialized && obj.initialized) {
					urlStoreInitialized = true;
					urlStoreDeferred.resolve(urlStoreInitialized);
				}
			}
		});

		return urlStoreDeferred.promise;
	},
};
